import React from "react";
import type { CachingUsage } from "../types";

interface PromptCachingCardProps {
  caching: CachingUsage;
  selectedMonth: string; // YYYY-MM (unused for now, kept for parity/future)
}

/** Prompt-cache summary for the selected month.
 *
 * Token reuse % = reused prefix tokens / all cacheable prefix tokens.
 * Cache-hit rate = cached evals / cache-eligible evals.
 * Both are sourced from evaluation_store (cache-eligible model cores only), so
 * the denominator is the cacheable prefix — not total billed tokens. Shows "—"
 * when there are no cache-eligible evaluations yet. */
export function PromptCachingCard({ caching }: PromptCachingCardProps) {
  const pct = (num: number, denom: number) =>
    denom > 0 ? `${((num / denom) * 100).toFixed(1)}%` : "—";
  const intFmt = (n: number) => n.toLocaleString();

  const tokenReuse = pct(caching.cached_tokens, caching.cacheable_tokens);
  const hitRate = pct(caching.cached_evaluations, caching.total_evaluations);

  return (
    <div className="rounded-lg border border-border bg-card shadow-sm h-full">
      <div className="p-5 flex flex-col h-full">
        <h2 className="text-lg font-semibold text-muted-foreground text-sm uppercase">
          Trace Caching
        </h2>
        <div className="mt-2 flex-1 flex flex-col justify-between">
          <div>
            <div className="flex justify-between items-end mb-1">
              <span className="font-mono text-3xl font-bold text-primary">
                {tokenReuse}
              </span>
              <span className="text-sm text-muted-foreground mb-1">tokens reused</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {intFmt(caching.cached_tokens)} of {intFmt(caching.cacheable_tokens)}{" "}
              cacheable prefix tokens
            </p>
            <div className="mt-3 flex justify-between text-xs">
              <span className="text-muted-foreground">Cache hit rate</span>
              <span className="font-mono font-semibold text-foreground">
                {hitRate}
                <span className="text-muted-foreground font-normal">
                  {" "}
                  ({intFmt(caching.cached_evaluations)}/
                  {intFmt(caching.total_evaluations)})
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
